import React from 'react';
import { CheckCircle, Hourglass, Package, ShieldCheck, Truck } from '@phosphor-icons/react';

import { StatusShell } from '../../design-system/components/shells';
import {
  StatusAction,
  StatusNext,
  StatusNextRow,
  StatusNote,
  StatusReceipt,
  StatusReceiptRow,
  StatusRedirect,
  StatusStack,
} from '../../design-system/patterns/status';
import { routes } from '../../routes/routes';
import { formatCurrency } from '../../utils/common';

/**
 * Payment success, the view. Pure: every input is a prop, every output a
 * callback.
 *
 * The container (`PaymentSuccess.jsx`) owns the Yoco verification call, the
 * order-status polling, the ~60s confirm budget, the cart clear, the review
 * modal and the redirect timer. None of it runs here, which is why the state
 * explorer can mount this file with fixtures and get the real screen back.
 *
 * THREE STATES, NOT ONE
 * ---------------------
 * The old page had two and told a small lie in both: it drew a green tick the
 * instant the redirect landed, before the backend had confirmed anything. This
 * one only claims a payment once `confirmed` is true.
 *
 *   pending    the disc is NEUTRAL. We do not know yet, so we do not colour it.
 *   timed out  still neutral, plus a note. The order is not lost, we just
 *              cannot confirm it on this screen inside the budget.
 *   confirmed  success disc, receipt, what happens next.
 *
 * WHAT THE COPY MAY NOT SAY
 * -------------------------
 * No courier, no city of origin, no shipping price (README section 9). Delivery
 * is nationwide and tracking is emailed; that is the whole of what we can
 * honestly promise from this screen.
 *
 * The button stays INK in all three states. A green "View order" would be using
 * colour to mean two things at once; the disc is already carrying the outcome.
 */

export function PaymentSuccessView({
  confirmed = false,
  timedOut = false,
  receipt = null,
  redirectIn = null,
}) {
  if (!confirmed) {
    return (
      <StatusShell
        tone="neutral"
        icon={Hourglass}
        title={timedOut ? 'Still confirming your payment' : 'Confirming your payment'}
        description={
          timedOut
            ? 'Your payment went through at the gateway. Confirming it on our side is taking longer than usual.'
            : 'Hold tight. This normally takes a few seconds.'
        }
        actions={
          <>
            <StatusAction to={routes.DASHBOARD}>Go to your dashboard</StatusAction>
            <StatusAction to={routes.MARKETPLACE} variant="ghost">
              Continue shopping
            </StatusAction>
            <StatusRedirect seconds={redirectIn} destination="your dashboard" />
          </>
        }
      >
        {timedOut && (
          <StatusNote>
            Nothing is lost. Your order will appear on your dashboard as soon as it settles, and
            the receipt still goes to your email.
          </StatusNote>
        )}
      </StatusShell>
    );
  }

  return (
    <StatusShell
      tone="success"
      icon={CheckCircle}
      title="Payment received"
      description="Your order is confirmed and the seller has been notified. A receipt is on its way to your email."
      actions={
        <>
          <StatusAction to={routes.DASHBOARD}>View your order</StatusAction>
          <StatusAction to={routes.MARKETPLACE} variant="ghost">
            Continue shopping
          </StatusAction>
          <StatusRedirect seconds={redirectIn} destination="your dashboard" />
        </>
      }
    >
      <StatusStack>
        {receipt && (
          <StatusReceipt>
            {receipt.orderNumber && (
              <StatusReceiptRow label="Order number" value={receipt.orderNumber} />
            )}
            {receipt.amount != null && (
              <StatusReceiptRow label="Amount paid" value={formatCurrency(receipt.amount)} total />
            )}
          </StatusReceipt>
        )}

        <StatusNext>
          <StatusNextRow icon={Package}>
            The seller packs your cards and hands them over for delivery.
          </StatusNextRow>
          <StatusNextRow icon={Truck}>
            Tracked delivery nationwide, with tracking emailed to you.
          </StatusNextRow>
          <StatusNextRow icon={ShieldCheck}>
            Buyer protection holds your payment until the order arrives.
          </StatusNextRow>
        </StatusNext>
      </StatusStack>
    </StatusShell>
  );
}

export default PaymentSuccessView;
